import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Injector,
  OnInit,
  input,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NGXLogger } from 'ngx-logger';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  Subscription,
  combineLatest,
  from,
  mergeMap,
  of,
} from 'rxjs';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';

import {
  SearchOptions,
  SearchPrompt,
  SearchProvider,
  createApiSearchPromptString,
} from 'models';
import { SEARCH_PROVIDER } from 'src/app/common/tokens';
import { ArchiveGridData } from 'src/app/models';
import { LayoutService } from 'src/app/services/layout.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { DatabasesQuery } from 'src/app/state/databases/databases.query';
import { GridSettingsService } from 'src/app/state/grid/grid-states.service';
import { SearchesQuery } from 'src/app/state/searches/searches.query';

import { toObservable } from '@angular/core/rxjs-interop';
import { AppConfigQuery } from 'src/app/modules/app-config';
import { SearchResultCacheService } from 'src/app/services/search-result-cache.service';
import { ArchiveResultCount } from './archive-result-count';

/**  */
@UntilDestroy()
@Component({
  selector: 'app-search-result-archive-selector',
  templateUrl: './search-result-archive-selector.component.html',
  styleUrls: ['./search-result-archive-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SearchResultArchiveSelectorComponent implements OnInit {
  /** The search result count for the active archive in the search. */
  activeArchiveCount = input.required<number>();
  /** Active archive id. */
  archive = this.archivesQuery.active;
  /** Archive result counts. */
  archiveResultCounts: ArchiveResultCount[] = [];
  /** Tracks if the result set is single, multiple, or no archives. */
  archiveSets: 'single' | 'multi' | 'none';
  /** Active database ID. */
  databaseId = this.databasesQuery.activeId;
  /** Whether search counts are enabled. Uses getter to avoid UI mismatch when toggled. */
  isSearchCountEnabled = this.appQuery.isSearchCountEnabled;
  /** Observable containing if currently viewed on handset. */
  isHandset$ = this.layout.isHandset$;
  /** Whether the archive counts are currently being loaded. */
  isLoading = true;
  /** Active search. */
  search = this.searchesQuery.active;
  /** Observable of whether a compact layout should be used. */
  useCompactLayout$ = this.layout.useCompactLayout$;
  /** Whether results with 0 count should be hidden. */
  hideArchivesWithNoResults$ = this.appQuery.hideArchivesWithNoResults$;

  activeArchiveCount$ = new BehaviorSubject<number>(0);
  private activeArchiveCountInput$ = toObservable(this.activeArchiveCount);
  private activeGridData: ArchiveGridData;
  private searchPrompts: SearchPrompt[];

  constructor(
    private logger: NGXLogger,
    private router: Router,
    private appQuery: ApplicationQuery,
    private appConfigQuery: AppConfigQuery,
    private archivesQuery: ArchivesQuery,
    private databasesQuery: DatabasesQuery,
    private searchesQuery: SearchesQuery,
    private searchResultCacheService: SearchResultCacheService,
    private layout: LayoutService,
    private gridStatesService: GridSettingsService,
    private injector: Injector,
    @Inject(SEARCH_PROVIDER) private searchProvider: SearchProvider
  ) {}

  ngOnInit(): void {
    this.listenForSearchParamChanges();
  }

  /**
   * Event handler for selecting a new archive.
   *
   * @param archiveId Archive ID.
   */
  onClickSelectArchive(archiveId: number): void {
    this.activeGridData.currentPage = 1;
    this.router.navigate(
      ['db', this.databaseId, 'archive', archiveId, 'search', this.search.id],
      {
        queryParams: {
          viewTab: undefined,
        },
        queryParamsHandling: 'merge',
      }
    );
  }

  /**
   * Reloads the search counts.
   */
  reloadSearchCounts(): void {
    this.isSearchCountEnabled = this.appQuery.isSearchCountEnabled;
    if (!this.isSearchCountEnabled) {
      // Do not load search counts, if not enabled.
      this.logger.debug(
        'Search counts will not be reloaded, as they are disabled by configuration.'
      );
      return;
    }
    this.logger.debug('Reloading search counts.');
    this.loadSearchCounts();
  }

  private getArchiveResultCount$(
    targetArchiveId: number,
    searchOptions: SearchOptions
  ): Observable<number> {
    searchOptions.targetArchiveId = targetArchiveId;
    return this.searchResultCacheService
      .run(this.databaseId, this.search, searchOptions)
      .pipe(
        map((response) => response.count),
        take(1)
      );
  }

  private listenForSearchParamChanges(): void {
    this.searchesQuery.searchRouteParams$
      .pipe(untilDestroyed(this))
      .subscribe(([databaseId, archiveId, searchId, searchPrompts]) =>
        this.onSearchParamsChange(
          databaseId ?? 0,
          archiveId ?? 0,
          searchId ?? 0,
          searchPrompts
        )
      );
  }

  private searchCountSubscribers: Subscription[] = [];

  private loadSearchCounts(): void {
    // Unsubscribe from any previous search count subscriptions to avoid memory leaks and duplicate requests.
    for (const sub of this.searchCountSubscribers) {
      sub.unsubscribe();
    }
    this.searchCountSubscribers.length = 0;

    this.isLoading = true;
    const searchOptions = {
      page: 1,
      countOnly: false,
      recordsPerPage: this.appQuery.archiveResultsPerPage,
      searchCriteria: this.searchPrompts
        ? createApiSearchPromptString(this.searchPrompts)
        : '',
      sort: '',
      tabId: 0,
      targetArchiveId: 0,
    };
    const queuedCountRequests: Array<{
      archiveId: number;
      count$: ReplaySubject<number>;
    }> = [];

    this.archiveResultCounts = this.search.archives.map((searchArchiveId) => {
      const archive = this.archivesQuery.getArchive(searchArchiveId);
      const isActive = this.archive.id === searchArchiveId;
      const count$ = new ReplaySubject<number>(1);

      queuedCountRequests.push({
        archiveId: searchArchiveId,
        count$: count$,
      });

      return new ArchiveResultCount(
        this.injector,
        archive.id,
        archive.name,
        isActive,
        count$.pipe(
          tap((count) =>
            this.logger.debug(
              `ARC - Loaded result count for archive ${archive.id}: ${count}`
            )
          )
        )
      );
    });

    // Subscribe to the active archive's count observable and update the BehaviorSubject
    const activeArchive = this.archiveResultCounts.find((arc) => arc.isActive);
    if (activeArchive !== undefined) {
      // This gets the correct total number for tab0 in tabbed archives, but it doesnt seem to cause extra queries regardless.
      const activeCountSub = activeArchive.count.subscribe((count) => {
        this.activeArchiveCount$.next(count ?? this.activeArchiveCount());
      });
      this.searchCountSubscribers.push(activeCountSub);
    } else {
      const inputSub = this.activeArchiveCountInput$.subscribe((count) => {
        this.logger.debug(`Active archive count: ${count}`);
        this.activeArchiveCount$.next(count);
      });
      this.searchCountSubscribers.push(inputSub);
    }

    this.updateArchiveSetsBasedOnCount();

    // Subscribe to the queued count requests and process them with a concurrency limit.
    const queuedCountRequestsSubscription = from(
      queuedCountRequests.sort((a, b) => {
        // Active archive should be processed first to ensure its count is available as soon as possible with tabbed archives. Then others by archiveId.
        if (a.archiveId === this.archive.id) return -1;
        if (b.archiveId === this.archive.id) return 1;
        return a.archiveId - b.archiveId;
      })
    )
      .pipe(
        mergeMap(
          ({ archiveId, count$ }) =>
            this.getArchiveResultCount$(archiveId, {
              ...searchOptions,
              targetArchiveId: archiveId,
            }).pipe(
              tap((count) =>
                this.logger.debug(
                  `Loaded result count for archive ${archiveId}: ${count}`
                )
              ),
              tap((count) => count$.next(count)),
              catchError((error) => {
                this.logger.error(
                  `Unable to load result count for archive ${archiveId}.`,
                  error
                );
                count$.next(0);
                return EMPTY;
              }),
              finalize(() => count$.complete())
            ),
          this.appConfigQuery.multiArchiveSearchConcurrentRequestsAllowed
        )
      )
      .subscribe();
    this.searchCountSubscribers.push(queuedCountRequestsSubscription);

    const counts$ = combineLatest(
      this.archiveResultCounts.map(
        (archiveSearchCount) => archiveSearchCount.count
      )
    );

    const archiveResultCountsSubscriber = combineLatest([
      counts$,
      this.appQuery.hideArchivesWithNoResults$.pipe(untilDestroyed(this)),
      this.appQuery.sortResultsArchivesByResultsCount$.pipe(
        untilDestroyed(this)
      ),
    ]).subscribe(
      ([
        _counts,
        _hideArchivesWithNoResults,
        sortResultsArchivesByResultsCount,
      ]) => {
        this.logger.debug(
          'Archive result counts have been loaded.',
          this.archiveResultCounts.map((arc) => ({
            archiveId: arc.archiveId,
            count: arc.count(),
          }))
        );

        // Use the counts to update the archive set display mode (single, multi, none)
        this.updateArchiveSetsBasedOnCount();

        if (sortResultsArchivesByResultsCount) {
          this.logger.debug(
            'Sort results archives by result is enabled. Sorting the archive result counts.'
          );

          this.archiveResultCounts = this.archiveResultCounts.sort(
            this.sortByResultCountDescending()
          );
        }
        if (_hideArchivesWithNoResults) {
          if (this.redirectToFirstArchiveWithResultsIfNeeded(true)) {
            return;
          }
        }
        this.isLoading = false;
      }
    );
    this.searchCountSubscribers.push(archiveResultCountsSubscriber);
  }

  private sortByResultCountDescending():
    | ((a: ArchiveResultCount, b: ArchiveResultCount) => number)
    | undefined {
    return (a, b) => {
      const aCount = a.count();
      const bCount = b.count();

      // If both counts are undefined, sort by archiveId
      if (aCount === undefined && bCount === undefined) {
        return a.archiveId - b.archiveId;
      }
      // If only a's count is undefined, a comes after b
      if (aCount === undefined) {
        return 1;
      }
      // If only b's count is undefined, b comes after a
      if (bCount === undefined) {
        return -1;
      }
      // Both counts are defined, sort by count descending
      return bCount - aCount;
    };
  }

  private onSearchParamsChange(
    databaseId: number,
    archiveId: number,
    searchId: number,
    searchPrompts: SearchPrompt[]
  ): void {
    this.databaseId = databaseId;
    this.archive = this.archivesQuery.getArchive(archiveId);
    this.search = this.searchesQuery.getSearch(searchId);
    this.searchPrompts = searchPrompts;
    this.archiveResultCounts = [];
    this.activeGridData = this.gridStatesService.getOrCreateArchiveGridData(
      this.databasesQuery.activeId,
      this.archive.id
    );
    if (databaseId && archiveId && searchId) {
      if (!this.appQuery.isSearchCountEnabled) {
        // Do not load search counts, if not enabled.
        // Just set the iterated counts list to the archive map.
        this.archiveResultCounts = this.search.archives.map((archiveId) => {
          const archive = this.archivesQuery.getArchive(archiveId);
          return new ArchiveResultCount(
            this.injector,
            archiveId,
            archive.name,
            archiveId === this.archive.id,
            of(1)
          );
        });
        this.updateArchiveSetsBasedOnCount();

        if (this.redirectToFirstArchiveWithResultsIfNeeded()) {
          return;
        }

        this.isLoading = false;
      } else {
        // Search counts are enabled, load them.
        this.loadSearchCounts();
      }
    }
  }

  /**
   * Redirects to the first available archive if no active archive is found or if the active archive has no results.
   *
   * @param requireNonZeroCount If true, redirects to an archive with count > 0 if the active archive has 0 results.
   * @returns True if a redirect was performed, false otherwise.
   */
  private redirectToFirstArchiveWithResultsIfNeeded(
    requireNonZeroCount: boolean = false
  ): boolean {
    const activeArchiveCount = this.archiveResultCounts.find(
      (rc) => rc.isActive
    );

    if (!activeArchiveCount) {
      this.logger.debug(
        'No active archive found. Redirecting to the first available archive.'
      );
      // No active archive found, redirect to the first in the list.
      if (this.archiveResultCounts.length === 0) {
        this.logger.error(
          'Unable to load archive result counts because no archives are accessible.'
        );
        return true;
      }

      const firstArchiveCount = this.archiveResultCounts[0];
      this.onClickSelectArchive(firstArchiveCount.archiveId);
      return true;
    }

    // If requireNonZeroCount is true, check if the active archive has 0 results
    if (requireNonZeroCount && activeArchiveCount.count() === 0) {
      this.logger.debug(
        'Active archive has no results. Searching for an archive with results.'
      );
      // Find the first archive with count > 0
      const archiveWithResults = this.archiveResultCounts.find(
        (rc) => rc.count()! > 0
      );

      if (archiveWithResults) {
        this.logger.debug(
          `Redirecting to archive ${archiveWithResults.archiveId} with ${archiveWithResults.count()} results.`
        );
        this.onClickSelectArchive(archiveWithResults.archiveId);
        return true;
      }
    }

    return false;
  }

  private updateArchiveSetsBasedOnCount() {
    let workingCounts = this.archiveResultCounts;
    this.appQuery.hideArchivesWithNoResults$
      .pipe(take(1))
      .subscribe((hideArchivesWithNoResults) => {
        if (hideArchivesWithNoResults) {
          workingCounts = this.archiveResultCounts.filter(
            // Account for any that have not returned results or have results greater than 0.
            (arc) => arc.count() === undefined || arc.count()! > 0
          );
        }
        switch (workingCounts.length) {
          case 0:
            this.archiveSets = 'none';
            break;
          case 1:
            this.archiveSets = 'single';
            break;
          default:
            this.archiveSets = 'multi';
            break;
        }
        this.logger.debug('Archive result set mode:', this.archiveSets);
      });
  }
}
